import { FC, useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { CustomChartPoint } from '@/entities/dashboards';
import { EmptyState } from '../../parts/EmptyState';
import { slicePaletteClass } from '../../utils';
import { ChartCardShell } from '../ChartCardShell';
import { PieSliceTooltip } from './PieSliceTooltip';
import { toPieSlices } from './pieSlices';

type Props = {
  title: string;
  points: CustomChartPoint[];
};

/**
 * Круговая диаграмма: доли категорий в целом.
 *
 * Круг занимает всю ширину диаграммы, а какой категории доля и сколько в ней — показывает
 * подсказка по наведению: постоянный перечень категорий повторял бы то, что уже нарисовано,
 * и отнимал бы у круга половину места.
 *
 * Рост круга ограничен сверху: на широкой доле ширины он вышел бы за отведённую диаграмме
 * высоту и потребовал прокрутки на ровном месте.
 */
export const PieChartView: FC<Props> = ({ title, points }) => {
  const slices = useMemo(() => toPieSlices(points), [points]);

  if (slices.length === 0) {
    return (
      <ChartCardShell title={title}>
        <EmptyState />
      </ChartCardShell>
    );
  }

  return (
    <ChartCardShell title={title}>
      <div className="aspect-square max-h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="label" isAnimationActive={false}>
              {slices.map((slice, index) => (
                <Cell key={slice.label} className={slicePaletteClass(index)} />
              ))}
            </Pie>
            <Tooltip content={<PieSliceTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </ChartCardShell>
  );
};
